import { z } from 'zod';
import { DateOnlySchema, IdSchema } from './common.ts';

/**
 * Where a machine is, and when (SCRUM-211).
 *
 * **A booking is not stored as a date range of its own.** It is a schedule
 * item carrying an equipment id, and the item's own `startDate` and
 * `endDate` are the booking. That is the whole reason this feature belongs
 * in Buildmate rather than behind an API: when somebody drags a bar, the
 * plant moves with it, and there is no parallel date range left behind to
 * drift. FleetTrack keeps `equipment_assignments` with its own dates
 * precisely because it has no schedule to hang them on.
 *
 * What is stored is this **mirror**, one document per machine per schedule
 * item, written by a trigger. It exists for one question — "where is EX-04
 * next Monday" — which has to be answered across every job at once.
 * Reading the schedule items directly cannot answer it: `scheduleItems`
 * live under a job and their rules require assignment to that job, so a
 * cross-job query is refused for anybody not on all of them. The same
 * reasoning produced `ownerBudgetLines` and `ownerChangeOrders`.
 *
 * Company-scoped, so the rules can gate it on `equipment: view` — which
 * the Field role holds — rather than per-job assignment.
 *
 * `jobName` and `title` are **copied**, like a notification's title. A
 * conflict warning that said "EX-04 is double-booked with job
 * 01J7Z…" would be useless, and joining back to the job is a read per row
 * on a query that exists to be fast.
 */
export const EquipmentBookingSchema = z.object({
  /** Derived: `${scheduleItemId}_${equipmentId}`. See `bookingId`. */
  id: IdSchema,
  companyId: IdSchema,
  equipmentId: IdSchema,
  jobId: IdSchema,
  scheduleItemId: IdSchema,
  /** Copied so a warning can name the job without a second read. */
  jobName: z.string().max(200),
  title: z.string().max(200),
  startDate: DateOnlySchema,
  endDate: DateOnlySchema,
  /**
   * Whether the schedule this booking came from is published.
   *
   * An offline schedule holds dates nobody has agreed to. Those bookings
   * still count for a **conflict warning** — the point is to catch the
   * clash while it is still a draft — but a report of what is on site
   * should say which are firm.
   */
  scheduleOnline: z.boolean().default(false),
  completed: z.boolean().default(false),
});
export type EquipmentBooking = z.infer<typeof EquipmentBookingSchema>;

/**
 * The document id for one machine on one schedule item.
 *
 * Derived rather than generated, so the trigger that maintains this mirror
 * can write it without first reading to find out whether it exists — and
 * so removing a machine from an item is a delete at a known path rather
 * than a query.
 */
export function bookingId(scheduleItemId: string, equipmentId: string): string {
  return `${scheduleItemId}_${equipmentId}`;
}

/** Two closed date ranges that share at least one day. */
export function overlaps(
  a: { readonly startDate: string; readonly endDate: string },
  b: { readonly startDate: string; readonly endDate: string },
): boolean {
  // Inclusive at both ends: an item running to the 14th and one starting on
  // the 14th want the same machine on the same day. ISO `YYYY-MM-DD`
  // strings compare correctly, so there is no parsing and no time zone.
  return a.startDate <= b.endDate && b.startDate <= a.endDate;
}

export interface BookingClash {
  readonly equipmentId: string;
  /** The booking being checked. */
  readonly booking: EquipmentBooking;
  /** What it runs into. Never itself, and never the same schedule item. */
  readonly against: EquipmentBooking;
  /** The days both want it, inclusive. */
  readonly from: string;
  readonly to: string;
}

/**
 * Where one booking runs into others.
 *
 * **Completed work is not a clash.** A machine booked on an item somebody
 * has already ticked off is not a machine anybody is waiting for, and
 * warning about last month's finished excavation would teach people to
 * ignore the warning — which is the only way this feature fails.
 *
 * Same schedule item, same machine is not a clash either: that is this
 * booking, or a re-read of it.
 */
export function clashesFor(
  booking: EquipmentBooking,
  all: readonly EquipmentBooking[],
): BookingClash[] {
  if (booking.completed) return [];
  return all
    .filter(
      (other) =>
        other.equipmentId === booking.equipmentId &&
        other.scheduleItemId !== booking.scheduleItemId &&
        !other.completed &&
        overlaps(booking, other),
    )
    .map((other) => ({
      equipmentId: booking.equipmentId,
      booking,
      against: other,
      from: booking.startDate > other.startDate ? booking.startDate : other.startDate,
      to: booking.endDate < other.endDate ? booking.endDate : other.endDate,
    }))
    .sort((a, b) => a.from.localeCompare(b.from));
}

/**
 * Every clash in a set of bookings, each pair reported once.
 *
 * Once, because "EX-04 is wanted by Framing and by Backfill" and "…by
 * Backfill and by Framing" are one problem, and a warning panel that
 * showed both would make two machines look like four.
 */
export function allClashes(all: readonly EquipmentBooking[]): BookingClash[] {
  const seen = new Set<string>();
  const out: BookingClash[] = [];
  for (const booking of all) {
    for (const clash of clashesFor(booking, all)) {
      const key = [clash.booking.id, clash.against.id].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(clash);
    }
  }
  return out.sort((a, b) => a.from.localeCompare(b.from));
}

/**
 * What is on a machine, or on every machine, across a date range.
 *
 * The shape behind "what equipment is being used next Monday": a range
 * rather than a day, because the same question is asked about a week.
 */
export function bookingsInRange(
  all: readonly EquipmentBooking[],
  range: { readonly startDate: string; readonly endDate: string },
): EquipmentBooking[] {
  return all
    .filter((booking) => overlaps(booking, range))
    .sort(
      (a, b) =>
        a.startDate.localeCompare(b.startDate) ||
        a.equipmentId.localeCompare(b.equipmentId) ||
        a.jobName.localeCompare(b.jobName),
    );
}

/**
 * The booking rows one schedule item produces.
 *
 * Shared by the Cloud Function trigger and the in-memory mirror that
 * stands in for it under `pnpm dev`, so the two cannot answer differently.
 * Two implementations of "what is booked" would eventually part company,
 * and the day they did, the warning would be right in production and
 * wrong on the machine somebody was developing against — or the reverse,
 * which is worse.
 */
export function bookingsForItem(
  item: {
    readonly id: string;
    readonly companyId: string;
    readonly jobId: string;
    readonly title: string;
    readonly startDate: string;
    readonly endDate: string;
    readonly completed?: boolean;
    readonly equipmentIds?: readonly string[];
  },
  job: { readonly name?: string; readonly scheduleOnline?: boolean },
): EquipmentBooking[] {
  return (item.equipmentIds ?? []).map((equipmentId) => ({
    id: bookingId(item.id, equipmentId),
    companyId: item.companyId,
    equipmentId,
    jobId: item.jobId,
    scheduleItemId: item.id,
    jobName: job.name ?? 'Unknown job',
    title: item.title,
    startDate: item.startDate,
    endDate: item.endDate,
    scheduleOnline: job.scheduleOnline === true,
    completed: item.completed === true,
  }));
}
